import React, { useEffect, useState, useMemo } from "react";
import TableTree, { Rows } from "@atlaskit/table-tree";
import DynamicTable from "@atlaskit/dynamic-table";
import Tabs, { Tab, TabList, TabPanel } from "@atlaskit/tabs";
import { renderTree, mergeCurrentActionToFlow } from "../../common/utils/integration.util";
import { useSelector, useDispatch } from "react-redux";
import lodash from "lodash";
import LogHeader from "./LogHeader";
import LogFilters from "./LogFilters";
import LogRow from "./LogRow";
import Modal, { ModalTransition, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "@atlaskit/modal-dialog";
import ExecutionLogDetails from "./ExecutionLogDetails";
import Button from "@atlaskit/button/new";
import { getExecutionLog, getLogItem, getAttachmentLogsForAction, clearAttachmentLogs } from "../../redux/actions/logActions";
import Spinner from "@atlaskit/spinner";
import Tooltip from "@atlaskit/tooltip";
import { useEscapeShortcut } from "../../common/utils/keybindings";
import { toggleLoading } from "../../redux/actions/messageActions";
import { selectFlow, setEditorSaved } from "../../redux/actions/integrationActions";
import ScriptedLogDetails from "./ScriptedLogDetails";
import { ActionType } from "../../common/interfaces/flow/action/ActionType";
import { formatDate } from "../../common/utils/formatter";
import { getJiraBaseURL } from "../../env/stage";
import ContextLogAppender from "./ContextLogAppender";

const ActionExecutionLog = ({ flow, action }) => {
  const dispatch = useDispatch();

  const [tree, setTree] = useState([]);
  const [details, setDetails] = useState(null);
  const [isOpen, setOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextPageTimestamp, setNextPageTimestamp] = useState(undefined);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
  const [contextLogAppender, setContextLogAppender] = useState(action?.contextLogAppender ?? "");

  const logs = useSelector((state) => state.integration.logs);
  const attachmentLogs = useSelector((state) => state.integration.attachmentLogs);
  const jiraBaseUrl = useMemo(() => getJiraBaseURL(), []);
  const attachmentTableHead = useMemo(
    () => ({
      cells: [
        { key: "filename", content: "Filename" },
        { key: "attachmentId", content: "Attachment ID" },
        { key: "issueKey", content: "Triggered Issue Key" },
        { key: "sentOn", content: "Sent On" },
      ],
    }),
    []
  );

  const attachmentTableRows = useMemo(() => {
    if (!attachmentLogs?.result) {
      return [];
    }
    return attachmentLogs.result.map((item, index) => {
      const sentTimestamp = item?.sendOn ?? item?.createdAt;
      const sentOn = sentTimestamp ? formatDate(sentTimestamp) : "";
      const issueLink = item?.context ? (
        <a href={`${jiraBaseUrl}/browse/${item.context}`} target="_blank" rel="noreferrer noopener">
          {item.context}
        </a>
      ) : (
        "-"
      );
      const attachmentLink = item?.context ? (
        <a href={`${jiraBaseUrl}/rest/api/3/attachment/${item?.attachmentId}`} target="_blank" rel="noreferrer noopener">
          {item?.attachmentId}
        </a>
      ) : (
        "-"
      );

      return {
        key: item?.attachmentKey ?? `${item?.attachmentId}-${index}`,
        cells: [
          { key: `filename-${index}`, content: item?.filename || item?.fileName || "-" },
          { key: `attachment-${index}`, content: attachmentLink },
          { key: `context-${index}`, content: issueLink },
          { key: `date-${index}`, content: sentOn || "-" },
        ],
      };
    });
  }, [attachmentLogs, jiraBaseUrl]);
  const hasAttachmentLogs = attachmentTableRows.length > 0;
  useEffect(() => {
    if (logs && logs.result) {
      const actionLogs = logs.result.filter((log) => log.actionId === action.id);
      const newTree = renderTree(actionLogs);

      if (!lodash.isEqual(tree, newTree) || (logs.currentTime && logs.currentTime > currentTime)) {
        setTree(newTree);
        setCurrentTime(logs.currentTime ? logs.currentTime : Date.now());
        //update the details page if active
        if (details) {
          setDetails(JSON.parse(JSON.stringify(details)));
        }
      }
      if (logs.hasOwnProperty("lastEvaluatedKey") && logs.lastEvaluatedKey !== undefined) {
        setNextPageTimestamp(logs.lastEvaluatedKey.timestamp);
      } else {
        setNextPageTimestamp(undefined);
      }
      setIsLoadingMore(false);
    }
  }, [logs, action]);

  useEffect(() => {
    dispatch(getExecutionLog(flow.id, null, null, null, action?.id));
    dispatch(toggleLoading(true, "Loading..."));
  }, [flow.id, action?.id]);

  useEffect(() => {
    if (!flow?.id || !action?.id) {
      dispatch(clearAttachmentLogs());
      return;
    }

    let isActive = true;
    setIsAttachmentLoading(true);
    const promise = dispatch(getAttachmentLogsForAction(flow.id, action.id));

    const stopLoading = () => {
      if (isActive) {
        setIsAttachmentLoading(false);
      }
    };

    if (promise?.finally) {
      promise.finally(stopLoading);
    } else {
      stopLoading();
    }

    return () => {
      isActive = false;
      dispatch(clearAttachmentLogs());
    };
  }, [dispatch, flow?.id, action?.id]);

  useEffect(() => {
    setContextLogAppender(action?.contextLogAppender ?? "");
  }, [action]);

  const onEscape = () => {
    if (isOpen) {
      setOpen(false);
      setDetails(null);
    }
  };
  useEscapeShortcut(onEscape);

  const debouncedContextLogUpdate = useMemo(
    () =>
      lodash.debounce((value) => {
        if (!flow || !action) {
          return;
        }
        const mergedFlow = mergeCurrentActionToFlow({ ...action, contextLogAppender: value }, flow);
        dispatch(selectFlow(mergedFlow));
        dispatch(setEditorSaved(false));
      }, 300),
    [flow, action]
  );

  useEffect(() => {
    return () => debouncedContextLogUpdate.cancel();
  }, [debouncedContextLogUpdate]);

  const onContextLogAppenderChange = (event) => {
    const value = event.target.value;
    setContextLogAppender(value);
    debouncedContextLogUpdate(value);
  };

  const loadMore = () => {
    setIsLoadingMore(true);
    dispatch(getExecutionLog(logs?.lastEvaluatedKey?.flowId, null, null, nextPageTimestamp, action?.id));
  };

  const showLogDetails = (details) => {
    if (details?.scope?.key) {
      dispatch(getLogItem(details?.flowId, details?.scope?.key));
    }
    setDetails(details);
    setOpen(true);
  };
  const modalTitle = details ? `Log for ${action?.name || details.actionId || "action"}` : "Execution Log Details";

  return (
    <div>
      <p>
        Here, you'll find detailed logs from each time the flow runs, providing insights into its activities and any issues that might have
        occurred.
      </p>
      <ContextLogAppender value={contextLogAppender} onChange={onContextLogAppenderChange} />
      <Tabs id="action-execution-log-tabs">
        <TabList>
          <Tab>Log Data</Tab>
          <Tab>Attachment History</Tab>
        </TabList>
        <TabPanel>
          <div className="ihub-log-tab-warpper">
            <div className="log-tab-margin-top" />
            <LogFilters isGlobal={false} flowId={flow.id} actionId={action?.id} showDelete={false} />
            <div className="execution-log">
              <TableTree>
                <LogHeader isGlobal={false} />
                <Rows
                  items={tree}
                  render={(logItem) => {
                    return <LogRow logItem={logItem} isGlobal={false} openDetailsLogview={showLogDetails} />;
                  }}
                />
              </TableTree>
            </div>
            {tree.length === 0 && <span className="ihub-log-noshow">No logs found, click 'Filter' to reload.</span>}
            {tree.length > 0 && (
              <div className="load-more-button-container">
                <span className="load-more-row-count">Number of rows: {tree.length}</span>
                {nextPageTimestamp && (
                  <Button className="load-more-button" onClick={loadMore} isDisabled={isLoadingMore}>
                    {isLoadingMore ? (
                      <>
                        <Spinner size="small" interactionName="load-more" label="Loading more logs" />
                        {" "}Loading...
                      </>
                    ) : (
                      "Load more"
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </TabPanel>
        <TabPanel>
          <div className="ihub-log-tab-warpper">
            <div className="attachment-history-table">
              {isAttachmentLoading ? (
                <div className="attachment-history-loading">
                  <Spinner interactionName="load" label="Loading attachments" />
                </div>
              ) : hasAttachmentLogs ? (
                <DynamicTable head={attachmentTableHead} rows={attachmentTableRows} rowsPerPage={10} defaultPage={1} />
              ) : (
                <p>No attachments have been sent for this action yet.</p>
              )}
            </div>
          </div>
        </TabPanel>
      </Tabs>
      <ModalTransition>
        {isOpen && (
          <Modal shouldCloseOnOverlayClick={false} width={1000} height={1000}>
            <ModalHeader>
              <ModalTitle>{modalTitle}</ModalTitle>
            </ModalHeader>
            <ModalBody>
              {action.type === ActionType.SCRIPT_ACTION_TYPE ? (
                <ScriptedLogDetails data={details} />
              ) : (
                <ExecutionLogDetails data={details} action={action} />
              )}
            </ModalBody>
            <ModalFooter>
              <Tooltip content="Press Escape to Close">
                <Button appearance="subtle" onClick={onEscape}>
                  Close
                </Button>
              </Tooltip>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
    </div>
  );
};
export default ActionExecutionLog;
